<script setup lang="ts">
import { computed } from "vue";

import ActionSource from "@/components/action/ActionSource.vue";
import SearchItem from "@/components/search/SearchItem.vue";
import { useActivityStore } from "@/store/activity";
import { useSearchMenuStore } from "@/store/search/searchMenu";
import { useWorkModeStore } from "@/store/workMode";

import TopMenuBoardDiff from "../ActivityPanel/TopMenuBoardDiff.vue";
import TopLeftMenu from "./TopLeftMenu.vue";
import TopRightMenu from "./TopRightMenu.vue";
import BreadCrumb from "./internal/BreadCrumb/BreadCrumb.vue";

const darkMode = computed(() => useWorkModeStore().isExecutionMode);

const boardActivityOpen = computed(() => useActivityStore().isBoardActivityOpen);
const searchOpen = computed(() => useSearchMenuStore().isSearchOpen);
</script>

<template>
  <header
    v-scrollable-on-board
    class="top-menu"
    :class="{ 'dark-mode': darkMode }"
    :aria-label="$t('label.menuTop')"
  >
    <ActionSource value="topbar-menu">
      <SearchItem v-if="searchOpen" />
      <TopMenuBoardDiff v-else-if="boardActivityOpen" />
      <template v-else>
        <TopLeftMenu class="left" />
        <BreadCrumb class="center" />
        <TopRightMenu class="right" />
      </template>
    </ActionSource>
  </header>
</template>

<style lang="scss">
@use "@/styles/variables";
@use "@/styles/variables/a11y" as colors-a11y;
@use "@/styles/variables/colors";
@use "@/styles/z-index";
@use "@/styles/global/theme-dark";

$bg-transition: 0.3s ease-in-out background-color;

.top-menu {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  background-color: colors.$background-ui;
  border-bottom: 1px solid colors.$border-subtle;
  height: variables.$top-header-height;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: $bg-transition;
  z-index: z-index.$menu + 1; // above side panel (for skip to search results button)

  & > div {
    display: flex;
    align-items: center;
    flex-shrink: 0;
  }

  &.dark-mode {
    @include theme-dark.theme-dark;
  }

  & > .left {
    position: absolute;
    left: 3px;
    z-index: z-index.$low;

    // Transition smoothly when overlapping the breadcrumbs (narrow screen)
    padding-right: 20px;
    /* stylelint-disable-next-line color-named */
    mask-image: linear-gradient(to left, transparent 0, black 20px);
    transition: $bg-transition;
  }

  & > .center {
    flex-grow: 1;
    overflow: hidden;
  }

  & > .right {
    position: absolute;
    right: 3px;
    justify-content: end;
    z-index: z-index.$low;

    // Transition smoothly when overlapping the breadcrumbs (narrow screen)
    padding-left: 20px;
    /* stylelint-disable-next-line color-named */
    mask-image: linear-gradient(to right, transparent 0, black 20px);
    transition: $bg-transition;
  }
}
</style>
